import React, { useEffect, useState } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { Button } from 'react-bootstrap';
import "../styles/PaymentForm.css";

function ApplePayButton({
  amountCents,
  currency = 'usd',
  country = 'US',
  clientSecret,
  onSuccess,
}) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!stripe || !clientSecret || !amountCents) return;

    const pr = stripe.paymentRequest({
      country,
      currency,
      total: { label: 'Donation', amount: amountCents },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setReady(true);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      );

      if (error) {
        ev.complete('fail');
        return;
      }
      ev.complete('success');

      if (paymentIntent?.status === 'requires_action') {
        const { error: actionError, paymentIntent: pi } =
          await stripe.confirmCardPayment(clientSecret);
        if (!actionError && pi?.status === 'succeeded') {
          onSuccess && onSuccess(pi);
        }
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess && onSuccess(paymentIntent);
      }
    });
  }, [stripe, clientSecret, amountCents, currency, country, onSuccess]);

  if (!stripe || !ready || !paymentRequest) return null;

  return (
    <div className="mb-3">
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'donate',
              theme: 'black',
              height: '44px',
            },
          },
        }}
      />
    </div>
  );
}

export default function PaymentElementForm({
  amount,
  onSuccess,
  clientSecret,
  currency = 'usd',
  country = 'US',
  onBack,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret || submitting) return;

    setError(null);
    setSubmitting(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message || 'Payment failed.');
      } else if (result.paymentIntent?.status === 'succeeded') {
        onSuccess && onSuccess(result.paymentIntent);
      } else {
        setError('Payment was not completed.');
      }
    } catch (err) {
      setError(err?.message || 'Payment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      {/* Scrollable content area */}
      <div className="payment-scroll">
        <ApplePayButton
          amountCents={amount}
          currency={currency}
          country={country}
          clientSecret={clientSecret}
          onSuccess={onSuccess}
        />

        <PaymentElement options={{ layout: 'tabs' }} />

        {error && <div className="text-danger mt-2">{error}</div>}
      </div>

      {/* Actions stay visible even when content scrolls */}
      <div className="payment-actions-inline mt-3">
        {onBack && (
          <Button
            type="button"
            variant="outline-secondary"
            onClick={onBack}
            disabled={submitting}
          >
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={!stripe || !elements || submitting}
          className="payment-primary-btn"
        >
          {submitting ? 'Processing…' : 'Donate Now'}
        </Button>
      </div>
    </form>
  );
}
